import { Form, Formik } from 'formik';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import callApi from '../../utils/callApi';
import { pushErrorTrackingEventFromErrorObject, pushErrorTrackingEventFromI18nKey } from '../../utils/tracking';
import Button from '../atoms/Button';
import SubHeadline from '../atoms/SubHeadline';
import PasswordField from '../fields/PasswordField';
import { showDefaultError, showNotification } from '../molecules/Notifications';

const EmbeddedPasswordReset = ({ passwordResetHash }: { passwordResetHash: string }) => {
  const { formatMessage } = useIntl();

  const [isSubmittedSuccessfully, setSubmittedSuccessfully] = useState(false);

  const resetPassword = (payload: { hash: string; password: string }) =>
    callApi({ method: 'PUT', endpoint: '/customer/password', payload, flow: 'legacy' });

  const initialValues = {
    password: ''
  };

  const onSubmit = async ({ password }: typeof initialValues) => {
    try {
      await resetPassword({ hash: passwordResetHash, password });
      setSubmittedSuccessfully(true);
      showNotification({
        level: 'success',
        text: formatMessage({ id: 'auth.notifications.passwordResetSuccess' }),
        closable: false
      });
    } catch (e: any) {
      console.error(e);
      if ([400, 404, 406].includes(e.statusCode)) {
        showNotification({
          level: 'error',
          text: formatMessage({ id: 'auth.notifications.passwordResetInvalid' }),
          autoClose: true,
          closable: true
        });
        pushErrorTrackingEventFromI18nKey('auth.notifications.passwordResetInvalid');
      } else {
        showDefaultError();
        pushErrorTrackingEventFromErrorObject(e, 'reset_password');
      }
    }
  };

  return (
    <div className="embedded-auth">
      <Formik initialValues={initialValues} onSubmit={onSubmit}>
        {({ isSubmitting }) => (
          <Form className="auth-form form">
            <div className="embedded-auth__header">
              <SubHeadline>
                <FormattedMessage id="auth.embedded.resetPassword" />
              </SubHeadline>
            </div>
            <div className="embedded-auth__body">
              <div className="form__row">
                <div className="form__field-container">
                  <PasswordField testIdPrefix="reset-password" type="passwordReset" />
                </div>
              </div>
              <div className="form__row">
                <div className="form__field-container">
                  <Button
                    type="submit"
                    data-test-id="reset-password-submit"
                    disabled={isSubmitting || isSubmittedSuccessfully}
                    isLoading={isSubmitting}
                  >
                    <FormattedMessage id="generic.save" />
                  </Button>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default EmbeddedPasswordReset;
